import { memo, useEffect, useRef, useState } from 'react';
import { NOTE_TEXT_MAX_LENGTH } from '@/constants/notes';
import { noteTextSchema } from '@/validation/noteSchema';
import styles from './Note.module.css';

interface Props {
  readonly text: string;
  readonly editing: boolean;
  readonly onCommit: (text: string) => void;
  readonly onCancel: () => void;
}

export const NoteEditor = memo(function NoteEditor({ text, editing, onCommit, onCancel }: Props) {
  const [draft, setDraft] = useState(text);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft(text);
      setError(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.select();
      });
    }
  }, [editing, text]);

  const commit = () => {
    const validated = noteTextSchema(draft);
    if (!validated.ok) {
      setError(validated.error[0]?.message ?? 'invalid text');
      return;
    }
    onCommit(validated.value);
  };

  if (!editing) {
    return (
      <div
        className={styles.body}
        role="textbox"
        aria-label="Note text (double-click or press Enter to edit)"
        tabIndex={-1}
      >
        {text ? (
          <p className={styles.text}>{text}</p>
        ) : (
          <p className={styles.placeholder}>Double-click to add text</p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.body} onPointerDown={(e) => e.stopPropagation()}>
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            commit();
          }
        }}
        maxLength={NOTE_TEXT_MAX_LENGTH}
        aria-label="Note text"
        aria-invalid={error !== null}
      />
      {error && (
        <p className={styles.errorText} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
